import React, { ChangeEvent, FC } from 'react';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { E164_PHONE_NUMBER_REGEX } from 'constants/assist/phoneNumbers';
import {
  AssistMigrationDistributionStorePreview,
  AssistMigrationStoreSection,
  FAQ,
} from 'types/assist/assistMigration';
import { EscalationEditor } from './EscalationEditor';
import { FaqEditor } from './FaqEditor';
import { HoursOfOperationEditor } from './HoursOfOperationEditor';
import { MachineInfoEditor } from './MachineInfoEditor';
import { ServiceCategoriesEditor } from './ServiceCategoriesEditor';

interface StoreDistributionEditorProps {
  section: AssistMigrationStoreSection;
  store: AssistMigrationDistributionStorePreview;
  globalFaqs?: FAQ[];
  onChange: (patch: Partial<AssistMigrationDistributionStorePreview>) => void;
}

const SectionTitle: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" style={{ fontWeight: 600, display: 'block', marginTop: 8 }}>
    {children}
  </Typography>
);

export const StoreDistributionEditor: FC<StoreDistributionEditorProps> = ({
  section,
  store,
  globalFaqs = [],
  onChange,
}) => {
  const readOnlyFaqQuestions = globalFaqs.map(({ question }) => question);
  const transferNumber = store.escalationPhoneNumber ?? '';
  const isTransferNumberValid =
    !transferNumber || transferNumber === 'N/A' || E164_PHONE_NUMBER_REGEX.test(transferNumber);

  const handleAlternateNamesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const alternateNames = event.target.value ? event.target.value.split('\n') : [];

    onChange({ alternateNames });
  };

  const handleTransferNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange({ escalationPhoneNumber: event.target.value || null });
  };

  if (section === 'hoursLocation') {
    return (
      <>
        <SectionTitle>Working hours</SectionTitle>
        <HoursOfOperationEditor
          value={store.hoursOfOperation}
          onChange={(hoursOfOperation) => onChange({ hoursOfOperation })}
        />

        <SectionTitle>Location details</SectionTitle>
        <TextField
          size="small"
          label="Alternate names (one per line)"
          value={store.alternateNames.join('\n')}
          fullWidth
          multiline
          style={{ marginBottom: 8 }}
          onChange={handleAlternateNamesChange}
        />
        <TextField
          size="small"
          label="Pronunciation"
          value={store.pronunciation ?? ''}
          fullWidth
          multiline
          style={{ marginBottom: 8 }}
          onChange={(event) => onChange({ pronunciation: event.target.value || null })}
        />
        <TextField
          size="small"
          label="Hours details"
          value={store.hoursDetails ?? ''}
          fullWidth
          multiline
          style={{ marginBottom: 8 }}
          onChange={(event) => onChange({ hoursDetails: event.target.value || null })}
        />
        <TextField
          size="small"
          label="Announcements"
          value={store.announcements ?? ''}
          fullWidth
          multiline
          onChange={(event) => onChange({ announcements: event.target.value || null })}
        />
      </>
    );
  }

  if (section === 'servicesPricing') {
    return (
      <>
        <ServiceCategoriesEditor
          value={store.serviceCategories}
          onChange={(serviceCategories) => onChange({ serviceCategories })}
        />
        <Divider style={{ margin: '12px 0' }} />
        <MachineInfoEditor
          label="Washers"
          value={store.washersInfo}
          onChange={(washersInfo) => onChange({ washersInfo })}
        />
        <MachineInfoEditor
          label="Dryers"
          value={store.dryersInfo}
          onChange={(dryersInfo) => onChange({ dryersInfo })}
        />
      </>
    );
  }

  if (section === 'faqs') {
    return (
      <>
        <SectionTitle>General FAQs</SectionTitle>
        <FaqEditor
          items={store.faqs}
          onChange={(faqs) => onChange({ faqs })}
          readOnlyQuestions={readOnlyFaqQuestions}
          allowReadOnlyRemoval
          readOnlyQuestionLabel="Global FAQ"
          readOnlyQuestionHelperText="This question comes from Global FAQs and cannot be edited for one location. Only the answer can be changed."
        />
        <SectionTitle>Location FAQs</SectionTitle>
        <FaqEditor
          items={store.faqsLocationDetails}
          onChange={(faqsLocationDetails) => onChange({ faqsLocationDetails })}
        />
        <SectionTitle>Hours FAQs</SectionTitle>
        <FaqEditor
          items={store.faqsHoursOfOperation}
          onChange={(faqsHoursOfOperation) => onChange({ faqsHoursOfOperation })}
        />
      </>
    );
  }

  return (
    <>
      <TextField
        size="small"
        label="Location transfer number"
        value={transferNumber}
        fullWidth
        error={!isTransferNumberValid}
        helperText={isTransferNumberValid ? 'Format: +19999999999 or N/A' : 'Invalid phone number'}
        style={{ marginBottom: 8 }}
        onChange={handleTransferNumberChange}
      />
      <EscalationEditor
        items={store.escalationRules}
        onChange={(escalationRules) => onChange({ escalationRules })}
      />
    </>
  );
};
